import React, { useCallback, useEffect, useState } from 'react';
import { Type } from 'lucide-react';

/**
 * TextSizeToggle — accessibility A / A+ / A++ button in the top nav.
 *
 * Cycles the base HTML font-size between 100% / 112.5% / 125% and
 * persists the choice in localStorage under `zi_text_size`. All rem-based
 * spacing in the app scales proportionally.
 *
 * Usage: <TextSizeToggle testid="text-size-toggle" />
 *
 * Renders as a compact pill in the top navigation, matching the
 * existing country / language / share cluster styling.
 */
const LEVELS = [
  { key: 'A',   size: '100%',   label: 'Default text size'   },
  { key: 'A+',  size: '112.5%', label: 'Larger text size'    },
  { key: 'A++', size: '125%',   label: 'Extra large text size' },
];

const STORAGE_KEY = 'zi_text_size';

const readSaved = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    const idx = LEVELS.findIndex((l) => l.key === v);
    return idx >= 0 ? idx : 0;
  } catch { return 0; }
};

const applyLevel = (idx) => {
  const lvl = LEVELS[idx];
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.style.fontSize = lvl.size;
  }
};

// Apply saved level once on module load so the size persists across page
// reloads and route changes without waiting for the component to mount.
if (typeof window !== 'undefined') {
  try { applyLevel(readSaved()); } catch { /* SSR-safe no-op */ }
}

export const TextSizeToggle = ({ testid = 'text-size-toggle', variant = 'nav' }) => {
  const [idx, setIdx] = useState(readSaved);

  useEffect(() => { applyLevel(idx); }, [idx]);

  const cycle = useCallback(() => {
    setIdx((cur) => {
      const next = (cur + 1) % LEVELS.length;
      try { localStorage.setItem(STORAGE_KEY, LEVELS[next].key); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const lvl = LEVELS[idx];

  if (variant === 'menu') {
    // Full-row variant used inside DesktopMoreMenu / mobile menu
    return (
      <button
        type="button"
        onClick={cycle}
        aria-label={`${lvl.label}. Click to change.`}
        data-testid={testid}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#EDF5F1] text-gold-100 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Type className="w-4 h-4 text-gold-400" />
          <span className="text-sm">Text size</span>
        </span>
        <span className="text-[11px] font-mono font-semibold tracking-wider text-gold-300">
          {lvl.key}
        </span>
      </button>
    );
  }

  // Compact top-nav pill
  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`${lvl.label}. Click to change.`}
      title={lvl.label}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-0.5 h-8 md:h-9 min-w-[38px] md:min-w-[44px] px-2 md:px-2.5 rounded-full bg-[#EDF5F1] border border-[#CBD2D9] text-[#0D1520] hover:bg-[#EDF5F1] hover:border-gold-500/40 transition-colors"
    >
      <Type className="w-3 h-3 md:w-3.5 md:h-3.5 text-gold-400" />
      <span className="text-[10.5px] md:text-[11px] font-mono font-semibold tracking-wider uppercase">
        {lvl.key}
      </span>
    </button>
  );
};

export default TextSizeToggle;
